import Button from '@cashew/button';
import React, { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Input, ModalConfirm, Tag } from '../../components';
import type { RootState } from '../../reducers';
import type { UserState } from '../../reducers/user';

const { api, priceFormat } = require('@cashew/utils');

export default ({
  orderDetails,
  showConfirmModal,
  setShowConfirmModal,
}: any) => {
  type Status = 'warning' | 'error' | 'info' | 'success' | 'none';
  const { t } = useTranslation();

  const history = useHistory();
  const { language, role }: UserState = useSelector(
    (state: RootState) => state.user
  );
  const [showResult, setShowResult] = useState(false);
  const [resultStatus, setResultStatus] = useState<Status>('none');
  const [shippingReference, setShippingReference] = useState('');
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchError, setDispatchError] = useState('');

  const orderReference =
    orderDetails.shopifyReference ?? orderDetails.orderReference;

  const resultTitle =
    resultStatus === 'success'
      ? 'Order was marked as dispatched'
      : 'Order dispatch status was not updated';

  const resultDescription =
    resultStatus === 'success' ? (
      <>
        {`The order ${orderDetails.id} was successfully marked as `}
        <Tag status="PAID">Dispatched</Tag>
      </>
    ) : (
      <>
        The status of the order &nbsp;
        <span style={{ fontWeight: '800' }}>{orderDetails.id}</span>
        &nbsp;was not updated.
      </>
    );

  const dispatchStatus = () => {
    setDispatchLoading(true);
    api
      .post({
        endpoint: `orders/${orderDetails.id}/dispatch`,
        params: { shippingReference },
      })
      .then(() => {
        setResultStatus('success');
      })
      .catch((error: any) => {
        setResultStatus('error');
        setDispatchError(t(error?.response?.data?.errorCode));
      })
      .finally(() => {
        setShowResult(true);
        setDispatchLoading(false);
      });
  };

  const confirmForm = (
    <div>
      {dispatchLoading && (
        <img className="loading-img" src="/loading.png" alt="Loading..." />
      )}
      <form className={dispatchLoading ? 'opacity-0' : ''}>
        <div className="form-row">
          <Input
            name="orderId"
            placeholder={t('orderDetails.orderId')}
            value={orderDetails.id.toString()}
            readonly
          />
          <div className="form-row-divider" />
          <Input
            name="grossOrderTotal"
            placeholder={t('orderDetails.grossOrderTotal')}
            value={priceFormat({
              currency: orderDetails.currencyCode,
              amount: orderDetails.totalAmount || 0,
            })}
            readonly
          />
        </div>
        <div className="form-row">
          <Input
            name="orderReference"
            placeholder={`${t('orderDetails.orderReference')}/${t(
              'orderDetails.merchantOrderCode'
            )}`}
            value={orderDetails.merchantOrderCode || orderReference}
            readonly
          />
          <div className="form-row-divider" />

          <Input
            name="customerName"
            placeholder={t('orderDetails.customerName')}
            value={`${
              orderDetails.customer?.firstName
                ? orderDetails.customer?.firstName
                : ''
            } ${
              orderDetails.customer?.lastName
                ? orderDetails.customer?.lastName
                : ''
            }`}
            readonly
          />
        </div>
        <div className="form-row">
          <div className="input-hint">
            <Input
              name="shippingReference"
              placeholder="Dispatch reference number"
              value={shippingReference}
              maxLength={20}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setShippingReference(e.target.value);
              }}
            />
            <span>Enter dispatch reference number to continue.</span>
          </div>
          <div className="form-row-divider" />
          <div style={{ width: '100%' }} />
        </div>
      </form>
    </div>
  );
  const isConfirmButtonDisable =
    shippingReference === '' || shippingReference.length < 4 || dispatchLoading;

  const confirmButtons = (
    <div style={{ display: 'flex' }}>
      <Button
        extraClass="btn-black secondary"
        onClick={() => {
          setResultStatus('warning');
          setShowResult(true);
        }}
      >
        Cancel
      </Button>
      <Button
        extraClass="order-details__confirm"
        disabled={isConfirmButtonDisable || role === 'CUSTOMER_CARE'}
        loading={dispatchLoading}
        onClick={() => {
          dispatchStatus();
        }}
      >
        {t('button.confirm')}
      </Button>
    </div>
  );

  const resultConfirmAction = () => {
    if (resultStatus === 'warning') {
      setResultStatus('none');
      setShowResult(false);
      setShowConfirmModal(false);
    } else {
      history.push(`/${language}/planned-purchase`);
    }
  };

  const resultButtons = (
    <div>
      <Button extraClass="btn-blue" onClick={resultConfirmAction}>
        Ok
      </Button>
    </div>
  );

  return (
    <ModalConfirm
      isVisible={showConfirmModal}
      onClose={() => {
        setShowConfirmModal(false);
      }}
      showCloseButton={false}
      confirmTitle={`Mark order ${orderDetails.id} as dispatched`}
      confirmDescription="Please review and confirm below"
      confirmForm={confirmForm}
      confirmButtons={confirmButtons}
      resultTitle={resultTitle}
      resultDescriptionComponent={resultDescription}
      resultButtons={resultButtons}
      showResult={showResult}
      resultStatus={resultStatus}
      dispatchError={dispatchError}
    />
  );
};
